import { Platform } from 'react-native';
import 'react-native-url-polyfill/auto';
import { createClient } from '@supabase/supabase-js';
import { ENV } from '@/config/env';
import type { Database } from './database.types';
import storageService from '@services/storage/storageService';
import { appStateService } from '@services/platform';

/**
 * Validate required environment variables
 */
if (!ENV.SUPABASE_URL || !ENV.SUPABASE_PUBLISHABLE_KEY) {
  throw new Error('Missing Supabase environment variables. Please check your .env.local file.');
}

/**
 * Supabase-compatible storage adapter
 * Wraps our unified storage service to match Supabase's expected interface
 */
const supabaseStorageAdapter = {
  getItem: async (key: string) => {
    return await storageService.getItem(key);
  },
  setItem: async (key: string, value: string) => {
    await storageService.setItem(key, value);
  },
  removeItem: async (key: string) => {
    await storageService.removeItem(key);
  },
};

/**
 * Supabase client instance
 * - Uses unified storage service for session persistence (works on all platforms)
 * - Auto-refreshes tokens when app is active
 * - Platform-aware configuration
 * - PKCE flow for enhanced security
 */
export const supabase = createClient<Database>(ENV.SUPABASE_URL, ENV.SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: supabaseStorageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: Platform.OS === 'web', // Enable for web OAuth redirects
    flowType: 'pkce', // Use PKCE flow for better security (prevents auth code interception)
  },
});

/**
 * CRITICAL: Auto-refresh session when app comes to foreground
 * Without this, users get logged out after ~1 hour
 *
 * This listener:
 * - Starts auto-refresh when app becomes active
 * - Stops auto-refresh when app goes to background
 * - Prevents unnecessary battery drain
 *
 * Works on all platforms:
 * - Native: Uses AppState (app foreground/background)
 * - Web: Uses window focus/blur events
 */
appStateService.addEventListener(state => {
  if (state === 'active') {
    supabase.auth.startAutoRefresh();
  } else {
    supabase.auth.stopAutoRefresh();
  }
});

// Export TypeScript database types
export type { Database } from './database.types';
